/**
 * @fileoverview optimized-api.ts - High-performance multi-chain NFT collection checker
 *
 * This file provides optimized parallel collection checking across multiple blockchains:
 * - Ethereum (Alchemy + Etherscan APIs)
 * - Base chain (Direct blockchain interaction)
 * - Ronin chain (Direct blockchain interaction)
 * - OpenSea shared contracts (Token ID range checking)
 *
 * Key features:
 * - Parallel API calls with configurable timeouts
 * - Progress tracking for UI updates
 * - Intelligent retry logic with exponential backoff
 * - Support for shared contracts via token ID ranges
 * - Configurable debug logging
 *
 * This file imports collection data and interfaces from api.ts but provides
 * the optimized implementation for production use.
 */

import { ethers } from "ethers"
import { withTimeout } from "@/lib/protection"
import { getCurrentProvider } from './ethereum-config'
import { fetchBaseChainNFTs as fetchBaseChainNFTsUtil } from "./base-chain"
import { fetchRoninChainNFTs } from "@/lib/ronin-chain"
import {
  COLLECTIONS,
  NFTCollection,
  manuallyCheckCollection,
  ALCHEMY_BASE_URL,
  ALCHEMY_BASE_CHAIN_URL,
  ALCHEMY_API_KEY,
  ALCHEMY_BASE_API_KEY,
  ETHERSCAN_API_KEY,
  BASESCAN_API_KEY,
  OPENSEA_API_KEY,
  ETHERSCAN_BASE_URL,
  OPENSEA_BASE_URL,
  OPENSEA_V2_BASE_URL,
  fetchDelegatedWallets,
  fetchDelegatedWalletsForIncoming,
  resolveEnsToAddress,
  mergeWalletResults,
  checkDelegationAll,
  type DelegateWallet,
  type Delegation,
  type DelegationType
} from "./api"

// Import interfaces from api.ts to avoid duplication
import type { AlchemyNFT, AlchemyResponse, EtherscanNFTResponse, OpenSeaAsset } from './api'

interface WalletSearchResult {
  walletAddress: string
  isMainWallet: boolean
  contractCounts: Record<string, number>
  tokenIdsByContract: Record<string, Set<string>>
}

const DEBUG_MODE = process.env.NODE_ENV === 'development' || process.env.DEBUG_OPTIMIZED_API === 'true'

// Constants for API timeouts and retry logic
const API_TIMEOUTS = {
  ALCHEMY: 30000,      // 30 seconds for Alchemy API
  ETHERSCAN: 20000,    // 20 seconds for Etherscan API
  BASE_CHAIN: 120000,  // 2 minutes for Base chain (slower)
  RONIN: 10000,        // 10 seconds for Ronin API
} as const

const PAGINATION_CONFIG = {
  PAGE_SIZE: 100,      // Alchemy API page size
  MAX_RETRIES: 3,      // Maximum retry attempts
  RETRY_DELAY: 1000,   // Base delay between retries (ms)
} as const

// Utility functions for common patterns

/**
 * Handles API response errors consistently across all API calls
 */
const handleApiError = (apiName: string, error: unknown): void => {
  console.error(`Error in ${apiName}:`, error)
}

/**
 * Creates a standardized error response for API failures
 */
const createErrorResponse = (message: string, error?: unknown) => ({
  ownedNfts: [],
  totalCount: 0,
  blockHash: "",
  error: message,
  details: error
})

/**
 * Updates progress callback with consistent formatting
 */
const updateProgress = (
  onProgress: ((progress: { current: number; total: number; collection: string }) => void) | undefined,
  current: number,
  total: number,
  collection: string
): void => {
  onProgress?.({ current, total, collection })
}

/**
 * Searches for NFT collections across multiple wallets including delegated ones
 *
 * @param walletAddresses - Array of wallet addresses to search
 * @param onProgress - Optional progress callback
 * @returns Promise<WalletSearchResult> - Combined search results
 */
async function searchMultipleWallets(
  walletAddresses: string[],
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<WalletSearchResult> {
  const walletResults: WalletSearchResult[] = []

  const totalWallets = walletAddresses.length
  let currentWalletIndex = 0

  for (const walletAddress of walletAddresses) {
    const isMainWallet = currentWalletIndex === 0
    const walletType = isMainWallet ? "main" : "delegated"

    // Calculate progress more evenly - wallet search takes 35% to 85% of total progress (50% range)
    const walletSearchProgress = 35 + Math.floor((currentWalletIndex / totalWallets) * 50)
    onProgress?.({
      current: walletSearchProgress,
      total: 100,
      collection: `Searching ${walletType === "delegated" ? "delegated" : walletType} wallet...`
    })

    console.log(`🔍 Searching ${walletType} wallet: ${walletAddress}`)

    try {
      // Run all API calls for this wallet in parallel
      const [alchemyResult, etherscanResult, baseResult, roninResult] = await Promise.allSettled([
        withTimeout(fetchAlchemyNFTs(walletAddress), API_TIMEOUTS.ALCHEMY),
        withTimeout(fetchEtherscanNFTs(walletAddress), API_TIMEOUTS.ETHERSCAN),
        withTimeout(fetchBaseChainNFTs(walletAddress), API_TIMEOUTS.BASE_CHAIN),
        withTimeout(fetchRoninChainNFTs(walletAddress), API_TIMEOUTS.RONIN)
      ])

      // Process results for this wallet
      const walletResult: WalletSearchResult = {
        walletAddress,
        isMainWallet,
        contractCounts: {},
        tokenIdsByContract: {}
      }

      // Process Alchemy results
      if (alchemyResult.status === 'fulfilled' && alchemyResult.value) {
        processAlchemyResults(alchemyResult.value, walletResult)
      }

      // Process Etherscan results
      if (etherscanResult.status === 'fulfilled' && etherscanResult.value) {
        processEtherscanResults(etherscanResult.value, walletResult)
      }

      // Process Base Chain results
      if (baseResult.status === 'fulfilled' && baseResult.value) {
        processBaseResults(baseResult.value, walletResult)
      }

      // Process Ronin Chain results
      if (roninResult.status === 'fulfilled' && roninResult.value) {
        processRoninResults(roninResult.value, walletResult)
      }

      // Process OpenSea collections for this wallet
      await processOpenSeaCollections(walletAddress, walletResult, onProgress)

      walletResults.push(walletResult)

      console.log(`✅ Completed search for ${walletType} wallet: ${walletAddress}`)
      console.log(`📊 Found ${Object.keys(walletResult.contractCounts).length} contracts`)

    } catch (error) {
      console.error(`❌ Error searching wallet ${walletAddress}:`, error)
    }

    currentWalletIndex++
  }

  // Merge all wallet results
  const mergedResult = mergeWalletResults(walletResults)

  onProgress?.({
    current: 90,
    total: 100,
    collection: "Finalizing results..."
  })

  return mergedResult
}

/**
 * Handles the case where user inputs a delegated wallet
 * Searches the main wallet (delegator) and checks permissions for the delegated wallet
 */
async function searchMainWalletForDelegatedWallet(
  mainWalletAddress: string,
  delegatedWalletAddress: string,
  delegatedWalletInfo: DelegateWallet,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<WalletSearchResult> {
  console.log(`🔍 Searching main wallet ${mainWalletAddress} for delegated wallet ${delegatedWalletAddress}`)

  // Step 1: Search the main wallet for all NFTs
  onProgress?.({
    current: 20,
    total: 100,
    collection: "Searching main wallet for NFTs..."
  })

  const [mainWalletResult] = await Promise.allSettled([
    searchSingleWallet(mainWalletAddress, true, onProgress)
  ])

  if (mainWalletResult.status !== 'fulfilled') {
    console.error(`❌ Failed to search main wallet:`, mainWalletResult.reason)
    return {
      walletAddress: delegatedWalletAddress,
      isMainWallet: false,
      contractCounts: {},
      tokenIdsByContract: {}
    }
  }

  const mainWalletNFTs = mainWalletResult.value
  console.log(`✅ Found ${Object.keys(mainWalletNFTs.contractCounts).length} contracts in main wallet`)

  // Step 2: Filter results based on delegation permissions
  onProgress?.({
    current: 50,
    total: 100,
    collection: "Checking delegation permissions..."
  })

  const filteredResult = await createDelegatedWalletResult(
    mainWalletNFTs,
    delegatedWalletInfo,
    mainWalletAddress
  )

  // Override the wallet address to show the delegated wallet
  filteredResult.walletAddress = delegatedWalletAddress

  console.log(`✅ Delegated wallet ${delegatedWalletAddress} has access to ${Object.keys(filteredResult.contractCounts).length} contracts`)

  onProgress?.({
    current: 80,
    total: 100,
    collection: "Finalizing results..."
  })

  return filteredResult
}

/**
 * Corrected delegation logic: search main wallet and check delegated wallet permissions
 * This is the proper way to handle delegation - find NFTs owned by main wallet,
 * then verify which delegated wallets have access to them
 */
async function searchMainWalletWithDelegationChecks(
  mainWalletAddress: string,
  delegatedWallets: DelegateWallet[],
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<WalletSearchResult> {
  console.log(`🔍 Searching main wallet ${mainWalletAddress} with delegation checks`)
  console.log(`🔗 Checking permissions for ${delegatedWallets.length} delegated wallets`)

  // Step 1: Search the main wallet for all NFTs
  onProgress?.({
    current: 15,
    total: 100,
    collection: "Searching main wallet for NFTs..."
  })

  const [mainWalletResult] = await Promise.allSettled([
    searchSingleWallet(mainWalletAddress, true, onProgress)
  ])

  if (mainWalletResult.status !== 'fulfilled') {
    console.error(`❌ Failed to search main wallet:`, mainWalletResult.reason)
    return {
      walletAddress: mainWalletAddress,
      isMainWallet: true,
      contractCounts: {},
      tokenIdsByContract: {}
    }
  }

  const mainWalletNFTs = mainWalletResult.value
  console.log(`✅ Found ${Object.keys(mainWalletNFTs.contractCounts).length} contracts in main wallet`)

  // Step 2: For each delegated wallet, check what NFTs they have access to
  onProgress?.({
    current: 50,
    total: 100,
    collection: "Checking delegation permissions..."
  })

  const walletResults: WalletSearchResult[] = [mainWalletNFTs]

  for (const delegatedWallet of delegatedWallets) {
    console.log(`🔍 Checking permissions for delegated wallet ${delegatedWallet.address}`)

    const delegatedWalletResult = await createDelegatedWalletResult(
      mainWalletNFTs,
      delegatedWallet,
      mainWalletAddress
    )

    walletResults.push(delegatedWalletResult)

    console.log(`✅ Delegated wallet ${delegatedWallet.address} has access to ${Object.keys(delegatedWalletResult.contractCounts).length} contracts`)
  }

  // Step 3: Merge all results
  onProgress?.({
    current: 80,
    total: 100,
    collection: "Merging results..."
  })

  const mergedResult = mergeWalletResults(walletResults)

  onProgress?.({
    current: 90,
    total: 100,
    collection: "Finalizing results..."
  })

  return mergedResult
}

/**
 * Creates a result for a delegated wallet based on main wallet NFTs and delegation permissions
 */
async function createDelegatedWalletResult(
  mainWalletNFTs: WalletSearchResult,
  delegatedWallet: DelegateWallet,
  mainWalletAddress: string
): Promise<WalletSearchResult> {
  const filteredContractCounts: Record<string, number> = {}
  const filteredTokenIdsByContract: Record<string, Set<string>> = {}

  // Check if delegation is ALL (full wallet access)
  const hasFullDelegation = delegatedWallet.permissions.some(p => p.type === "ALL")

  if (hasFullDelegation) {
    console.log(`✅ Full delegation found for ${delegatedWallet.address} - full access granted`)
    return {
      walletAddress: delegatedWallet.address,
      isMainWallet: false,
      contractCounts: { ...mainWalletNFTs.contractCounts },
      tokenIdsByContract: Object.fromEntries(
        Object.entries(mainWalletNFTs.tokenIdsByContract).map(([contract, tokenIds]) => [
          contract,
          new Set(tokenIds)
        ])
      )
    }
  }

  // Check contract-level delegations
  const contractDelegations = delegatedWallet.permissions.filter(p =>
    p.type === "CONTRACT" && p.contract
  )

  if (contractDelegations.length > 0) {
    console.log(`✅ Contract delegations found for ${delegatedWallet.address}: ${contractDelegations.length}`)

    for (const [contractAddress, tokenIds] of Object.entries(mainWalletNFTs.tokenIdsByContract)) {
      const hasContractAccess = contractDelegations.some(p =>
        p.contract!.toLowerCase() === contractAddress.toLowerCase()
      )

      if (hasContractAccess) {
        filteredContractCounts[contractAddress] = mainWalletNFTs.contractCounts[contractAddress] || 0
        filteredTokenIdsByContract[contractAddress] = tokenIds
        console.log(`✅ Contract access granted for ${contractAddress}`)
      }
    }
  }

  // Check token-level delegations
  const tokenDelegations = delegatedWallet.permissions.filter(p =>
    p.type === "TOKEN" && p.contract && p.tokenId !== null
  )

  if (tokenDelegations.length > 0) {
    console.log(`✅ Token delegations found for ${delegatedWallet.address}: ${tokenDelegations.length}`)

    for (const [contractAddress, tokenIds] of Object.entries(mainWalletNFTs.tokenIdsByContract)) {
      const contractTokenDelegations = tokenDelegations.filter(p =>
        p.contract!.toLowerCase() === contractAddress.toLowerCase()
      )

      if (contractTokenDelegations.length > 0) {
        const delegatedTokenIds = new Set<string>()

        contractTokenDelegations.forEach(delegation => {
          if (delegation.tokenId !== null) {
            delegatedTokenIds.add(delegation.tokenId.toString())
          }
        })

        // Filter tokens to only include delegated ones
        const filteredTokenIds = new Set<string>()
        for (const tokenId of tokenIds) {
          if (delegatedTokenIds.has(tokenId)) {
            filteredTokenIds.add(tokenId)
          }
        }

        if (filteredTokenIds.size > 0) {
          filteredContractCounts[contractAddress] = (filteredContractCounts[contractAddress] || 0) + filteredTokenIds.size
          if (!filteredTokenIdsByContract[contractAddress]) {
            filteredTokenIdsByContract[contractAddress] = new Set()
          }
          for (const tokenId of filteredTokenIds) {
            filteredTokenIdsByContract[contractAddress].add(tokenId)
          }
          console.log(`✅ Token access granted for ${contractAddress} - ${filteredTokenIds.size} tokens`)
        }
      }
    }
  }

  if (Object.keys(filteredContractCounts).length === 0) {
    console.log(`⚠️ No access granted for delegated wallet ${delegatedWallet.address}`)
  }

  return {
    walletAddress: delegatedWallet.address,
    isMainWallet: false,
    contractCounts: filteredContractCounts,
    tokenIdsByContract: filteredTokenIdsByContract
  }
}

/**
 * Check if wallet owns an ENS domain and return it as an NFT
 * This is a fallback for when Alchemy/Etherscan don't detect ENS domains
 */
async function checkENSOwnership(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  console.log(`🔍 [ENS] Checking ENS ownership for ${walletAddress}`)
  
  try {
    const provider = getCurrentProvider()
    console.log(`🔍 [ENS] Provider created for ${walletAddress}`)
    
    // Check if wallet has a primary ENS name
    console.log(`🔍 [ENS] Looking up ENS name for ${walletAddress}`)
    const ensName = await provider.lookupAddress(walletAddress)
    console.log(`🔍 [ENS] ENS lookup result for ${walletAddress}: ${ensName}`)
    
    if (!ensName) {
      console.log(`🔍 [ENS] No ENS name found for ${walletAddress}`)
      return [] // No ENS name found
    }
    
    console.log(`🔍 [ENS] Found ENS name for ${walletAddress}: ${ensName}`)
    
    // Calculate namehash for the ENS name
    const nameHash = ethers.namehash(ensName)
    console.log(`🔍 [ENS] Namehash for ${ensName}: ${nameHash}`)
    
    // Return as NFT data
    const result = [{
      contractAddress: "0x57f1887a8bf19b14fc0df6fd9b2acc9af147ea85", // ENS Registry
      tokenId: nameHash
    }]
    
    console.log(`🔍 [ENS] Returning ENS NFT data for ${walletAddress}:`, result)
    return result
    
  } catch (error) {
    console.error(`🔍 [ENS] Error checking ENS ownership for ${walletAddress}:`, error)
    return []
  }
}

/**
 * Helper function to search a single wallet
 */
async function searchSingleWallet(
  walletAddress: string,
  isMainWallet: boolean,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<WalletSearchResult> {
  const walletType = isMainWallet ? "main" : "delegated"

  onProgress?.({
    current: Math.floor((isMainWallet ? 20 : 60) + Math.random() * 20),
    total: 100,
    collection: `Searching ${walletType} wallet ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
  })

  console.log(`🔍 Searching ${walletType} wallet: ${walletAddress}`)

  try {
    // Run all API calls for this wallet in parallel
    const [alchemyResult, etherscanResult, baseResult, roninResult] = await Promise.allSettled([
      withTimeout(fetchAlchemyNFTs(walletAddress), API_TIMEOUTS.ALCHEMY),
      withTimeout(fetchEtherscanNFTs(walletAddress), API_TIMEOUTS.ETHERSCAN),
      withTimeout(fetchBaseChainNFTs(walletAddress), API_TIMEOUTS.BASE_CHAIN),
      withTimeout(fetchRoninChainNFTs(walletAddress), API_TIMEOUTS.RONIN)
    ])

    // Process results for this wallet
    const walletResult: WalletSearchResult = {
      walletAddress,
      isMainWallet,
      contractCounts: {},
      tokenIdsByContract: {}
    }

    // Process Alchemy results
    if (alchemyResult.status === 'fulfilled' && alchemyResult.value) {
      processAlchemyResults(alchemyResult.value, walletResult)
    }

    // Process Etherscan results
    if (etherscanResult.status === 'fulfilled' && etherscanResult.value) {
      processEtherscanResults(etherscanResult.value, walletResult)
    }

    // Process Base Chain results
    if (baseResult.status === 'fulfilled' && baseResult.value) {
      processBaseResults(baseResult.value, walletResult)
    }

    // Process Ronin Chain results
    if (roninResult.status === 'fulfilled' && roninResult.value) {
      processRoninResults(roninResult.value, walletResult)
    }

    // Process OpenSea collections for this wallet
    await processOpenSeaCollections(walletAddress, walletResult, onProgress)

    // Check for ENS ownership as fallback
    try {
      const ensNfts = await checkENSOwnership(walletAddress)
      if (ensNfts.length > 0) {
        console.log(`🔍 Found ${ensNfts.length} ENS domain(s) for ${walletAddress}`)
        
        // Process ENS NFTs as if they came from Alchemy
        ensNfts.forEach(nft => {
          const contractAddress = nft.contractAddress.toLowerCase()
          
          if (!walletResult.contractCounts[contractAddress]) {
            walletResult.contractCounts[contractAddress] = 0
            walletResult.tokenIdsByContract[contractAddress] = []
          }
          
          walletResult.contractCounts[contractAddress]++
          walletResult.tokenIdsByContract[contractAddress].push(nft.tokenId)
        })
      }
    } catch (error) {
      console.error(`Error checking ENS ownership for ${walletAddress}:`, error)
    }

    console.log(`✅ Completed search for ${walletType} wallet: ${walletAddress}`)
    console.log(`📊 Found ${Object.keys(walletResult.contractCounts).length} contracts`)

    return walletResult

  } catch (error) {
    console.error(`❌ Error searching wallet ${walletAddress}:`, error)
    return {
      walletAddress,
      isMainWallet,
      contractCounts: {},
      tokenIdsByContract: {}
    }
  }
}

/**
 * Enhanced version of searchMultipleWallets that respects delegation types
 * This function intelligently searches wallets based on their delegation permissions
 */
async function searchMultipleWalletsWithDelegationAwareness(
  mainWalletAddress: string,
  walletAddresses: string[],
  delegatedWallets: DelegateWallet[],
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<WalletSearchResult> {
  const walletResults: WalletSearchResult[] = []

  const totalWallets = walletAddresses.length
  let currentWalletIndex = 0

  for (const walletAddress of walletAddresses) {
    const isMainWallet = currentWalletIndex === 0
    const walletType = isMainWallet ? "main" : "delegated"

    // Find delegation info for this wallet
    const delegationInfo = delegatedWallets.find(wallet =>
      wallet.address.toLowerCase() === walletAddress.toLowerCase()
    )

    // Calculate progress more evenly - wallet search takes 35% to 85% of total progress (50% range)
    const walletSearchProgress = 35 + Math.floor((currentWalletIndex / totalWallets) * 50)
    onProgress?.({
      current: walletSearchProgress,
      total: 100,
      collection: `Searching ${walletType === "delegated" ? "delegated" : walletType} wallet...`
    })

    console.log(`🔍 Searching ${walletType} wallet: ${walletAddress}`)
    if (delegationInfo && DEBUG_MODE) {
      console.log(`🔗 Delegation type: ${delegationInfo.type}`)
      console.log(`📋 Permissions: ${delegationInfo.permissions.length} delegations`)
    }

    try {
      // Run all API calls for this wallet in parallel
      const [alchemyResult, etherscanResult, baseResult, roninResult] = await Promise.allSettled([
        withTimeout(fetchAlchemyNFTs(walletAddress), API_TIMEOUTS.ALCHEMY),
        withTimeout(fetchEtherscanNFTs(walletAddress), API_TIMEOUTS.ETHERSCAN),
        withTimeout(fetchBaseChainNFTs(walletAddress), API_TIMEOUTS.BASE_CHAIN),
        withTimeout(fetchRoninChainNFTs(walletAddress), API_TIMEOUTS.RONIN)
      ])

      // Process results for this wallet
      const walletResult: WalletSearchResult = {
        walletAddress,
        isMainWallet,
        contractCounts: {},
        tokenIdsByContract: {}
      }

      // Process Alchemy results
      if (alchemyResult.status === 'fulfilled' && alchemyResult.value) {
        processAlchemyResults(alchemyResult.value, walletResult)
      }

      // Process Etherscan results
      if (etherscanResult.status === 'fulfilled' && etherscanResult.value) {
        processEtherscanResults(etherscanResult.value, walletResult)
      }

      // Process Base Chain results
      if (baseResult.status === 'fulfilled' && baseResult.value) {
        processBaseResults(baseResult.value, walletResult)
      }

      // Process Ronin Chain results
      if (roninResult.status === 'fulfilled' && roninResult.value) {
        processRoninResults(roninResult.value, walletResult)
      }

      // Process OpenSea collections for this wallet
      await processOpenSeaCollections(walletAddress, walletResult, onProgress)

      // Check for ENS ownership as fallback
      try {
        const ensNfts = await checkENSOwnership(walletAddress)
        if (ensNfts.length > 0) {
          console.log(`🔍 Found ${ensNfts.length} ENS domain(s) for ${walletAddress}`)
          
          // Process ENS NFTs as if they came from Alchemy
          ensNfts.forEach(nft => {
            const contractAddress = nft.contractAddress.toLowerCase()
            
            if (!walletResult.contractCounts[contractAddress]) {
              walletResult.contractCounts[contractAddress] = 0
              walletResult.tokenIdsByContract[contractAddress] = []
            }
            
            walletResult.contractCounts[contractAddress]++
            walletResult.tokenIdsByContract[contractAddress].push(nft.tokenId)
          })
        }
      } catch (error) {
        console.error(`Error checking ENS ownership for ${walletAddress}:`, error)
      }

      // For delegated wallets, search normally - we want to find their NFTs too
      // The delegation permissions will be checked during collection matching
      if (!isMainWallet && delegationInfo) {
        console.log(`🔍 Searching delegated wallet ${walletAddress} for NFTs`)
        console.log(`📋 Delegation type: ${delegationInfo.type}`)
        console.log(`📋 Delegation permissions: ${delegationInfo.permissions.length}`)

        // Log delegation details for debugging
        if (delegationInfo.permissions.length > 0) {
          delegationInfo.permissions.forEach((perm, index) => {
            console.log(`  Permission ${index + 1}: ${perm.type} ${perm.from} → ${perm.to}${perm.contract ? ` for ${perm.contract}` : ''}`)
          })
        }
      }

      walletResults.push(walletResult)

      console.log(`✅ Completed search for ${walletType} wallet: ${walletAddress}`)
      console.log(`📊 Found ${Object.keys(walletResult.contractCounts).length} contracts`)

    } catch (error) {
      console.error(`❌ Error searching wallet ${walletAddress}:`, error)
    }

    currentWalletIndex++
  }

  // Check for ENS ownership on main wallet as fallback
  console.log(`🔍 [ENS] About to check ENS ownership for main wallet: ${mainWalletAddress}`)
  await checkENSOwnershipForMainWallet(mainWalletAddress, walletResults)
  console.log(`🔍 [ENS] Completed ENS check for main wallet: ${mainWalletAddress}`)

  // Merge all wallet results
  const mergedResult = mergeWalletResults(walletResults)

  onProgress?.({
    current: 90,
    total: 100,
    collection: "Finalizing results..."
  })

  return mergedResult
}

/**
 * Check for ENS ownership on main wallet as fallback
 */
async function checkENSOwnershipForMainWallet(
  mainWalletAddress: string,
  walletResults: WalletSearchResult[]
): Promise<void> {
  try {
    console.log(`🔍 [ENS] Checking ENS ownership for main wallet: ${mainWalletAddress}`)
    const ensNfts = await checkENSOwnership(mainWalletAddress)
    if (ensNfts.length > 0) {
      console.log(`🔍 [ENS] Found ${ensNfts.length} ENS domain(s) for main wallet`)
      
      // Add ENS to the first wallet result (main wallet)
      if (walletResults.length > 0) {
        const mainWalletResult = walletResults[0]
        ensNfts.forEach(nft => {
          const contractAddress = nft.contractAddress.toLowerCase()
          const tokenId = nft.tokenId
          
          // Add to contract counts
          if (!mainWalletResult.contractCounts[contractAddress]) {
            mainWalletResult.contractCounts[contractAddress] = 0
          }
          mainWalletResult.contractCounts[contractAddress]++
          
          // Add to token IDs
          if (!mainWalletResult.tokenIdsByContract[contractAddress]) {
            mainWalletResult.tokenIdsByContract[contractAddress] = []
          }
          mainWalletResult.tokenIdsByContract[contractAddress].push(tokenId)
        })
        
        console.log(`🔍 [ENS] Added ENS data to main wallet result`)
      }
    } else {
      console.log(`🔍 [ENS] No ENS domains found for main wallet`)
    }
  } catch (error) {
    console.error(`❌ [ENS] Error checking ENS ownership:`, error)
  }
}

/**
 * Enhanced delegation logic for wallet search results
 * This ensures proper handling of delegated wallet permissions
 */
function filterResultsByDelegation(
  walletResult: WalletSearchResult,
  delegationInfo: DelegateWallet,
  mainWalletAddress: string
): void {
  console.log(`🔍 Processing delegation for ${walletResult.walletAddress}`)

  // Check if delegation is ALL (full wallet access)
  const hasFullDelegation = delegationInfo.permissions.some(p => p.type === "ALL")

  if (hasFullDelegation) {
    console.log(`✅ Full delegation found for ${walletResult.walletAddress} - full access granted`)
    console.log(`📊 Wallet has ${Object.keys(walletResult.contractCounts).length} contracts before filtering`)
    console.log(`🔍 Since delegation is ALL, no filtering needed - delegated wallet has full access`)
    // For full delegation, the delegated wallet has access to all NFTs from main wallet
    // But we still need to check if this wallet has its own NFTs that it should have access to
    return
  }

  // Check contract-level delegations
  const contractDelegations = delegationInfo.permissions.filter(p =>
    p.type === "CONTRACT" && p.contract
  )

  if (contractDelegations.length > 0) {
    console.log(`✅ Contract delegations found for ${walletResult.walletAddress}: ${contractDelegations.length}`)

    // Filter results to only include contracts that are delegated
    const filteredContractCounts: Record<string, number> = {}
    const filteredTokenIdsByContract: Record<string, Set<string>> = {}

    for (const [contractAddress, tokenIds] of Object.entries(walletResult.tokenIdsByContract)) {
      const hasContractAccess = contractDelegations.some(p =>
        p.contract!.toLowerCase() === contractAddress.toLowerCase()
      )

      if (hasContractAccess) {
        filteredContractCounts[contractAddress] = walletResult.contractCounts[contractAddress] || 0
        filteredTokenIdsByContract[contractAddress] = tokenIds
        console.log(`✅ Contract access granted for ${contractAddress}`)
      }
    }

    // Update the wallet result with filtered data
    walletResult.contractCounts = filteredContractCounts
    walletResult.tokenIdsByContract = filteredTokenIdsByContract

    console.log(`🔄 Contract filtering complete: ${Object.keys(filteredContractCounts).length} contracts accessible`)
  } else {
    console.log(`⚠️ No contract delegations found - no NFT access`)
    // No contract delegations, so no access to any NFTs
    walletResult.contractCounts = {}
    walletResult.tokenIdsByContract = {}
  }
}

/**
 * Processes Alchemy API results for a single wallet
 */
function processAlchemyResults(alchemyData: any, walletResult: WalletSearchResult): void {
  if (!alchemyData.ownedNfts) return

  alchemyData.ownedNfts.forEach((nft: any) => {
    if (!nft?.contract?.address || !nft?.id?.tokenId) return

    const contractAddress = nft.contract.address.toLowerCase()
    const tokenId = nft.id.tokenId

    if (!walletResult.tokenIdsByContract[contractAddress]) {
      walletResult.tokenIdsByContract[contractAddress] = new Set()
    }

    if (!walletResult.tokenIdsByContract[contractAddress].has(tokenId)) {
      walletResult.tokenIdsByContract[contractAddress].add(tokenId)
      walletResult.contractCounts[contractAddress] = (walletResult.contractCounts[contractAddress] || 0) + 1
    }
  })
}

/**
 * Processes Etherscan API results for a single wallet
 */
function processEtherscanResults(etherscanData: any[], walletResult: WalletSearchResult): void {
  etherscanData.forEach((item) => {
    if (!item.contractAddress || !item.tokenId) return

    const contractAddress = item.contractAddress.toLowerCase()
    const tokenId = item.tokenId

    if (!walletResult.tokenIdsByContract[contractAddress]) {
      walletResult.tokenIdsByContract[contractAddress] = new Set()
    }

    if (!walletResult.tokenIdsByContract[contractAddress].has(tokenId)) {
      walletResult.tokenIdsByContract[contractAddress].add(tokenId)
      walletResult.contractCounts[contractAddress] = (walletResult.contractCounts[contractAddress] || 0) + 1
    }
  })
}

/**
 * Processes Base chain API results for a single wallet
 */
function processBaseResults(baseData: any[], walletResult: WalletSearchResult): void {
  baseData.forEach((item) => {
    if (!item.contractAddress || !item.tokenId) return

    const contractAddress = item.contractAddress.toLowerCase()
    const tokenId = item.tokenId

    if (!walletResult.tokenIdsByContract[contractAddress]) {
      walletResult.tokenIdsByContract[contractAddress] = new Set()
    }

    if (!walletResult.tokenIdsByContract[contractAddress].has(tokenId)) {
      walletResult.tokenIdsByContract[contractAddress].add(tokenId)
      walletResult.contractCounts[contractAddress] = (walletResult.contractCounts[contractAddress] || 0) + 1
    }
  })
}

/**
 * Processes Ronin chain API results for a single wallet
 */
function processRoninResults(roninData: any[], walletResult: WalletSearchResult): void {
  roninData.forEach((item) => {
    if (!item.contractAddress || !item.tokenId) return

    const contractAddress = item.contractAddress.toLowerCase()
    const tokenId = item.tokenId

    if (!walletResult.tokenIdsByContract[contractAddress]) {
      walletResult.tokenIdsByContract[contractAddress] = new Set()
    }

    if (!walletResult.tokenIdsByContract[contractAddress].has(tokenId)) {
      walletResult.tokenIdsByContract[contractAddress].add(tokenId)
      walletResult.contractCounts[contractAddress] = (walletResult.contractCounts[contractAddress] || 0) + 1
    }
  })
}

/**
 * Processes OpenSea collections for a single wallet
 */
async function processOpenSeaCollections(
  walletAddress: string,
  walletResult: WalletSearchResult,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<void> {
  try {
    const specialIds = new Set(["transmissions909", "toyfaces"])
    const openSeaCollections = COLLECTIONS.filter((c) => {
      if (!c.openSeaSlug) return false
      if (c.chain === "base") return false
      return !!c.tokenIdRange || specialIds.has(c.id)
    })

    for (const collection of openSeaCollections) {
      if (!collection.openSeaSlug) continue

      const assets = await fetchOpenSeaAssets(walletAddress, collection.openSeaSlug)

      if (assets.length > 0) {
        const contractAddress = collection.contractAddress.toLowerCase()

        if (!walletResult.tokenIdsByContract[contractAddress]) {
          walletResult.tokenIdsByContract[contractAddress] = new Set()
        }

        assets.forEach((asset) => {
          const tokenId = (asset as any).token_id || (asset as any).tokenId

          if (!walletResult.tokenIdsByContract[contractAddress].has(tokenId)) {
            walletResult.tokenIdsByContract[contractAddress].add(tokenId)
            walletResult.contractCounts[contractAddress] = (walletResult.contractCounts[contractAddress] || 0) + 1
          }
        })
      }
    }
  } catch (error) {
    console.error("❌ Error processing OpenSea collections:", error)
  }
}

// Debug logging for API keys
console.log("🔧 [Optimized API] Environment check:", {
  hasAlchemyKey: !!(process.env.ALCHEMY_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_API_KEY),
  hasOpenseaKey: !!(process.env.OPENSEA_API_KEY || process.env.NEXT_PUBLIC_OPENSEA_API_KEY),
  hasEtherscanKey: !!(process.env.ETHERSCAN_API_KEY || process.env.NEXT_PUBLIC_ETHERSCAN_API_KEY),
  alchemyKey: (process.env.ALCHEMY_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_API_KEY) ? "Set" : "Not set",
  openseaKey: (process.env.OPENSEA_API_KEY || process.env.NEXT_PUBLIC_OPENSEA_API_KEY) ? "Set" : "Not set",
  etherscanKey: (process.env.ETHERSCAN_API_KEY || process.env.NEXT_PUBLIC_ETHERSCAN_API_KEY) ? "Set" : "Not set"
})

// Copy the individual API functions from api.ts
/**
 * Fetches NFTs from Alchemy API for a given wallet address
 *
 * @param walletAddress - The wallet address to fetch NFTs for
 * @returns Promise<AlchemyResponse> - Object containing owned NFTs, total count, and block hash
 *
 * Handles pagination automatically if more than 100 NFTs are found.
 * Uses retry logic with exponential backoff for failed requests.
 */
async function fetchAlchemyNFTs(walletAddress: string): Promise<AlchemyResponse> {
  try {
    const url = `${ALCHEMY_BASE_URL}${ALCHEMY_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=${PAGINATION_CONFIG.PAGE_SIZE}`
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Alchemy API error: ${response.status}`)
    }

    const data = await response.json()

    if (DEBUG_MODE) {
      console.log(`Alchemy found ${data.totalCount} NFTs for wallet ${walletAddress}`)
    }

    // If there are more than PAGE_SIZE NFTs, we need to paginate
    if (data.totalCount > PAGINATION_CONFIG.PAGE_SIZE) {
      let allNfts = [...data.ownedNfts]
      let pageKey = data.pageKey
      let retryCount = 0
      const maxRetries = PAGINATION_CONFIG.MAX_RETRIES

      while (pageKey && retryCount < maxRetries) {
        try {
          const pageUrl = `${ALCHEMY_BASE_URL}${ALCHEMY_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=${PAGINATION_CONFIG.PAGE_SIZE}&pageKey=${pageKey}`
          const pageResponse = await fetch(pageUrl)

          if (!pageResponse.ok) {
            console.warn(`Error fetching page with pageKey ${pageKey}: ${pageResponse.status}`)
            break
          }

          const pageData = await pageResponse.json()
          allNfts = [...allNfts, ...pageData.ownedNfts]
          pageKey = pageData.pageKey

          if (DEBUG_MODE) {
            console.log(`Fetched additional page with ${pageData.ownedNfts.length} NFTs`)
          }
          
          retryCount = 0
        } catch (error) {
          console.warn(`Error fetching page with pageKey ${pageKey}:`, error)
          retryCount++
          
          if (retryCount >= maxRetries) {
            console.warn(`Max retries (${maxRetries}) reached for pagination. Returning partial results.`)
            break
          }
          
          await new Promise(resolve => setTimeout(resolve, PAGINATION_CONFIG.RETRY_DELAY * retryCount))
        }
      }

      return {
        ownedNfts: allNfts,
        totalCount: allNfts.length,
        blockHash: data.blockHash,
      }
    }

    return data
  } catch (error) {
    console.error("Error in fetchAlchemyNFTs:", error)
    return {
      ownedNfts: [],
      totalCount: 0,
      blockHash: "",
    }
  }
}

async function fetchEtherscanNFTs(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  const tokenData: Array<{ contractAddress: string; tokenId: string }> = []

  try {
    // Fetch ERC-721 tokens (NFTs) from Etherscan
    const erc721Url = `${ETHERSCAN_BASE_URL}?module=account&action=tokennfttx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${ETHERSCAN_API_KEY}`
    const erc721Response = await fetch(erc721Url)

    if (!erc721Response.ok) {
      throw new Error(`Etherscan API error: ${erc721Response.status}`)
    }

    const erc721Data: EtherscanNFTResponse = await erc721Response.json()

    if (DEBUG_MODE) {
      console.log("Etherscan ERC-721 response status:", erc721Data.status)
      console.log("Etherscan ERC-721 result count:", erc721Data.result?.length || 0)
    }

    if (erc721Data.status === "1" && Array.isArray(erc721Data.result)) {
      const holdings = new Map<string, Set<string>>()

      erc721Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (
          (tx as any).contractAddress || (tx as any).TokenAddress || ""
        ).toLowerCase()
        const tokenId = (tx as any).tokenID || (tx as any).TokenId || ""

        if (!contractAddress || !tokenId) return

        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-721 tokens from Etherscan:", error)
  }

  try {
    // Fetch ERC-1155 tokens from Etherscan
    const erc1155Url = `${ETHERSCAN_BASE_URL}?module=account&action=token1155tx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${ETHERSCAN_API_KEY}`
    const erc1155Response = await fetch(erc1155Url)

    if (!erc1155Response.ok) {
      throw new Error(`Etherscan API error: ${erc1155Response.status}`)
    }

    const erc1155Data: EtherscanNFTResponse = await erc1155Response.json()

    if (DEBUG_MODE) {
      console.log("Etherscan ERC-1155 response status:", erc1155Data.status)
      console.log("Etherscan ERC-1155 result count:", erc1155Data.result?.length || 0)
    }

    if (erc1155Data.status === "1" && Array.isArray(erc1155Data.result)) {
      const holdings = new Map<string, Set<string>>()

      erc1155Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (
          (tx as any).contractAddress || (tx as any).TokenAddress || ""
        ).toLowerCase()
        const tokenId = (tx as any).tokenID || (tx as any).TokenId || ""

        if (!contractAddress || !tokenId) return

        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-1155 tokens from Etherscan:", error)
  }

  return tokenData
}

export async function fetchBaseChainNFTs(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  console.log(`[FETCH-BASE] Starting fetchBaseChainNFTs for wallet: ${walletAddress}`)
  console.log(`[FETCH-BASE] typeof window: ${typeof window}`)
  
  try {
    // In browser → call server API to avoid exposing Alchemy key and CORS
    if (typeof window !== 'undefined') {
      console.log(`[FETCH-BASE] Browser environment, calling API`)
      const res = await fetch(`/api/base-nfts?wallet=${encodeURIComponent(walletAddress)}`)
      if (!res.ok) {
        console.warn(`Base NFTs API error: ${res.status}`)
        return []
      }
      const json = await res.json()
      const nfts = (json?.nfts ?? []) as Array<{ contractAddress: string; tokenId: string }>
      console.log(`[FETCH-BASE] API returned ${nfts.length} NFTs`)
      return nfts
    }
  } catch (e) {
    console.warn('Error calling /api/base-nfts, falling back to direct util:', e)
  }
  
  // On server or if API fails → use server-side util directly
  console.log(`[FETCH-BASE] Server environment, calling fetchBaseChainNFTsUtil`)
  const result = await fetchBaseChainNFTsUtil(walletAddress)
  console.log(`[FETCH-BASE] fetchBaseChainNFTsUtil returned ${result.length} NFTs`)
  return result
}

/**
 * Fetches NFT assets from OpenSea API for a specific collection
 *
 * @param walletAddress - The wallet address to check for ownership
 * @param collectionSlug - The OpenSea collection slug (e.g., "transmissions909")
 * @returns Promise<OpenSeaAsset[]> - Array of NFT assets owned by the wallet
 *
 * Uses the internal API endpoint instead of direct OpenSea calls to avoid CORS issues
 * and API key exposure in the browser environment.
 */
async function fetchOpenSeaAssets(walletAddress: string, collectionSlug: string): Promise<OpenSeaAsset[]> {
  try {
    console.log(`Fetching ${collectionSlug} assets via our API for ${walletAddress}...`)

    // Normalize the collection slug to ensure consistent format
    const normalizedSlug = collectionSlug.toLowerCase().trim()

    // Use our API endpoint instead of direct OpenSea calls
    const response = await fetch(`/api/opensea-collection?owner=${walletAddress}&collection=${normalizedSlug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.warn(`API error (${response.status}): ${response.statusText}`)
      return []
    }

    const result = await response.json()

    const rawAssets: any[] = Array.isArray(result.assets)
      ? result.assets
      : Array.isArray(result.data)
      ? result.data
      : []

    if (rawAssets.length > 0) {
      console.log(`Found ${rawAssets.length} ${normalizedSlug} assets via API`)

      // Normalize to OpenSeaAsset-like shape
      return rawAssets.map((nft: any) => ({
        token_id: nft.tokenId || nft.identifier || nft.token_id || nft?.id?.tokenId,
        image_url: nft.image || nft.image_url || nft?.metadata?.image || nft?.media?.[0]?.gateway,
        name: nft.name || nft.title || (nft.tokenId ? `#${nft.tokenId}` : undefined),
        collection: { slug: normalizedSlug },
      }))
    }
    console.warn(`No assets found for ${normalizedSlug} via API`)
    return []
  } catch (error) {
    console.error(`Error fetching ${collectionSlug} assets via API:`, error)
    return []
  }
}

/**
 * Main function that checks wallet NFT collections across multiple blockchains in parallel
 *
 * @param walletAddress - The wallet address to check for NFT ownership
 * @param onProgress - Optional callback function to track progress (0-100%)
 * @returns Promise<NFTCollection[]> - Array of owned collections with token counts
 *
 * This function performs parallel API calls to:
 * - Alchemy API (Ethereum mainnet)
 * - Etherscan API (Ethereum transactions)
 * - Base chain API (Base network)
 * - Ronin chain API (Ronin network)
 *
 * Results are then matched against the COLLECTIONS array to determine ownership.
 */
export async function checkCollectionsInParallelWithProgress(
  walletAddress: string,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<NFTCollection[]> {
  console.log(`Starting TRUE parallel collection check for ${walletAddress}`)

  try {
    // Step 1: Initialize (5%)
    onProgress?.({
      current: 5,
      total: 100,
      collection: "Initializing..."
    })

    // Step 2: Start API calls (15%)
    onProgress?.({
      current: 15,
      total: 100,
      collection: "Starting API calls..."
    })

    // Run all API calls in parallel with configured timeouts
    const [alchemyResult, etherscanResult, baseResult, roninResult] = await Promise.allSettled([
      withTimeout(fetchAlchemyNFTs(walletAddress), API_TIMEOUTS.ALCHEMY),
      withTimeout(fetchEtherscanNFTs(walletAddress), API_TIMEOUTS.ETHERSCAN),
      withTimeout(fetchBaseChainNFTs(walletAddress), API_TIMEOUTS.BASE_CHAIN),
      withTimeout(fetchRoninChainNFTs(walletAddress), API_TIMEOUTS.RONIN)
    ])

    // Debug: Log all results
    if (DEBUG_MODE) {
      console.log(`🔍 [DEBUG] Promise.allSettled results:`)
      console.log(`  - Alchemy: ${alchemyResult.status}`)
      console.log(`  - Etherscan: ${etherscanResult.status}`)
      console.log(`  - Base: ${baseResult.status}`)
      console.log(`  - Ronin: ${roninResult.status}`)
    }
    
    if (baseResult.status === 'fulfilled') {
      if (DEBUG_MODE) {
        console.log(`🔍 [DEBUG] Base result length: ${baseResult.value?.length || 0}`)
        const ilyaNfts = baseResult.value?.filter(nft =>
          nft.contractAddress.toLowerCase() === "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9"
        ) || []
        console.log(`🔍 [DEBUG] Ilya NFTs in Base result: ${ilyaNfts.length}`)
      }
    } else {
      if (DEBUG_MODE) {
        console.log(`🔍 [DEBUG] Base result error: ${baseResult.reason}`)
      }
    }

    // Step 3: Process results (granular updates)
    onProgress?.({ current: 30, total: 100, collection: "Processing Alchemy results..." })

    // Create a map to count NFTs by contract address
    const contractCounts: Record<string, number> = {}
    const allFoundContracts = new Set<string>()
    const tokenIdsByContract: Record<string, Set<string>> = {}
    const contractChains: Record<string, "ethereum" | "base" | "ronin"> = {}

    // Process Alchemy results
    if (alchemyResult.status === 'fulfilled' && alchemyResult.value) {
      const alchemyData = alchemyResult.value
      console.log(`✅ Alchemy found ${alchemyData.totalCount} total NFTs`)
      if (alchemyData.ownedNfts) {
        console.log(`📊 Processing ${alchemyData.ownedNfts.length} Alchemy NFTs`)
        alchemyData.ownedNfts.forEach((nft) => {
          if (!nft || !nft.contract || !nft.contract.address || !nft.id || !nft.id.tokenId) {
            return
          }

          const contractAddress = nft.contract.address.toLowerCase()
          const tokenId = nft.id.tokenId

          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          if (!tokenIdsByContract[contractAddress].has(tokenId)) {
            tokenIdsByContract[contractAddress].add(tokenId)
            contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
            allFoundContracts.add(contractAddress)
            contractChains[contractAddress] = "ethereum"
          }
        })
        console.log(`📈 After Alchemy: ${Object.keys(contractCounts).length} unique contracts found`)
      }
    } else {
      console.log(`❌ Alchemy failed: ${alchemyResult.status}`)
    }
    onProgress?.({ current: 40, total: 100, collection: "Processing Etherscan results..." })

    // Process Etherscan results
    if (etherscanResult.status === 'fulfilled' && etherscanResult.value) {
      console.log(`✅ Etherscan found ${etherscanResult.value.length} NFTs`)
      etherscanResult.value.forEach((item) => {
        if (!item.contractAddress || !item.tokenId) return

        const contractAddress = item.contractAddress.toLowerCase()
        const tokenId = item.tokenId

        if (!tokenIdsByContract[contractAddress]) {
          tokenIdsByContract[contractAddress] = new Set()
        }

        if (!tokenIdsByContract[contractAddress].has(tokenId)) {
          tokenIdsByContract[contractAddress].add(tokenId)
          contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
          allFoundContracts.add(contractAddress)
          contractChains[contractAddress] = "ethereum"
        }
      })
      console.log(`📈 After Etherscan: ${Object.keys(contractCounts).length} unique contracts found`)
    } else {
      console.log(`❌ Etherscan failed: ${etherscanResult.status}`)
    }
    onProgress?.({ current: 48, total: 100, collection: "Processing Base chain results..." })

    // Process Base Chain results
    if (baseResult.status === 'fulfilled' && baseResult.value) {
      console.log(`✅ Base Chain found ${baseResult.value.length} NFTs`)
      baseResult.value.forEach((item) => {
        if (!item.contractAddress || !item.tokenId) return

        const contractAddress = item.contractAddress.toLowerCase()
        const tokenId = item.tokenId

        // Debug logging for Ilya Kazakov contract
        if (contractAddress === "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9" && DEBUG_MODE) {
          console.log(`🎯 [DEBUG] Found Ilya Kazakov Base NFT: ${contractAddress} - Token ID: ${tokenId}`)
        }

        if (!tokenIdsByContract[contractAddress]) {
          tokenIdsByContract[contractAddress] = new Set()
        }

        if (!tokenIdsByContract[contractAddress].has(tokenId)) {
          tokenIdsByContract[contractAddress].add(tokenId)
          contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
          allFoundContracts.add(contractAddress)
          contractChains[contractAddress] = "base"
          
          // Debug logging for Ilya Kazakov contract
          if (contractAddress === "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9" && DEBUG_MODE) {
            console.log(`🎯 [DEBUG] Added to contractCounts: ${contractAddress} = ${contractCounts[contractAddress]}`)
          }
        }
      })
      console.log(`📈 After Base Chain: ${Object.keys(contractCounts).length} unique contracts found`)
      
      // Debug: Check if Ilya contract is in contractCounts
      const ilyaContract = "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9"
      if (DEBUG_MODE) {
        if (contractCounts[ilyaContract]) {
          console.log(`🎯 [DEBUG] Ilya contract found in contractCounts: ${ilyaContract} = ${contractCounts[ilyaContract]}`)
        } else {
          console.log(`❌ [DEBUG] Ilya contract NOT found in contractCounts: ${ilyaContract}`)
        }

        // Debug: Log all contract addresses
        console.log(`🔍 [DEBUG] All contract addresses:`, Object.keys(contractCounts))
      }
    } else {
      console.log(`❌ Base Chain failed: ${baseResult.status}`)
    }
    onProgress?.({ current: 52, total: 100, collection: "Processing Ronin chain results..." })

    // Process Ronin Chain results
    if (roninResult.status === 'fulfilled' && roninResult.value) {
      console.log(`✅ Ronin Chain found ${roninResult.value.length} NFTs`)
      roninResult.value.forEach((item) => {
        if (!item.contractAddress || !item.tokenId) return

        const contractAddress = item.contractAddress.toLowerCase()
        const tokenId = item.tokenId

        if (!tokenIdsByContract[contractAddress]) {
          tokenIdsByContract[contractAddress] = new Set()
        }

        if (!tokenIdsByContract[contractAddress].has(tokenId)) {
          tokenIdsByContract[contractAddress].add(tokenId)
          contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
          allFoundContracts.add(contractAddress)
          contractChains[contractAddress] = "ronin"
        }
      })
      console.log(`📈 After Ronin Chain: ${Object.keys(contractCounts).length} unique contracts found`)
    } else {
      console.log(`❌ Ronin Chain failed: ${roninResult.status}`)
    }

    console.log(`🎯 Total unique contracts found: ${Object.keys(contractCounts).length}`)
    console.log(`📋 Contract addresses:`, Array.from(allFoundContracts))

    // Step 4: Direct OpenSea API integration (granular between 55–75)
    onProgress?.({ current: 55, total: 100, collection: "Checking OpenSea collections..." })

    // DIRECT OpenSea API integration for collections with openSeaSlug
    try {
      // Only call OpenSea when it's actually useful:
      // - collections on shared OpenSea contract (have tokenIdRange)
      // - specific special-cases we hardcode
      // - never for Base-chain collections (handled via Base APIs)
      const specialIds = new Set(["transmissions909", "toyfaces"]) // grifters handled strictly on-chain only
      const openSeaCollections = COLLECTIONS.filter((c) => {
        if (!c.openSeaSlug) return false
        if (c.chain === "base") return false
        return !!c.tokenIdRange || specialIds.has(c.id)
      })
      console.log(`🔍 Checking ${openSeaCollections.length} collections with OpenSea slugs (filtered)`) 

      const totalOpenSea = Math.max(openSeaCollections.length, 1)
      let idx = 0
      for (const collection of openSeaCollections) {
        if (!collection.openSeaSlug) continue

        console.log(`🔍 Directly checking OpenSea for ${collection.name} (${collection.openSeaSlug})...`)

        // Fetch assets directly from OpenSea API
        const assets = await fetchOpenSeaAssets(walletAddress, collection.openSeaSlug)

        if (assets.length > 0) {
          console.log(`✅ Found ${assets.length} ${collection.name} assets from OpenSea API`)

          // Get the contract address
          const contractAddress = collection.contractAddress.toLowerCase()

          // Initialize token ID set if it doesn't exist
          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          // Add each token to our tracking
          assets.forEach((asset) => {
            const tokenId = (asset as any).token_id || (asset as any).tokenId

            if (!tokenIdsByContract[contractAddress].has(tokenId)) {
              tokenIdsByContract[contractAddress].add(tokenId)
              contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
              allFoundContracts.add(contractAddress)
              contractChains[contractAddress] = "ethereum"

              console.log(`➕ Added ${collection.name} token ${tokenId} from OpenSea API`)
            }
          })
        } else {
          console.log(`❌ No ${collection.name} assets found from OpenSea API`)
        }
        // Progress between 55 and 75 during OpenSea checks
        idx++
        const pct = 55 + Math.floor((20 * idx) / totalOpenSea)
        onProgress?.({ current: Math.min(pct, 75), total: 100, collection: "Checking OpenSea collections..." })
      }
      
      console.log(`📈 After OpenSea: ${Object.keys(contractCounts).length} unique contracts found`)
    } catch (error) {
      console.error("❌ Error in direct OpenSea API integration:", error)
    }

    // Note: Removed Grifters sanitization logic (replaced with Lil Pudgys)

    // Step 5: Check collections with full logic (75%)
    onProgress?.({ current: 75, total: 100, collection: "Matching collections..." })

    // If we couldn't get any data from any API, fall back to direct checks
    if (Object.keys(contractCounts).length === 0) {
      console.warn("No NFT data found from APIs, trying direct collection checks...")
      
      // Try direct contract checks for popular collections
      const ownedCollections: NFTCollection[] = []
      
      for (const baseCollection of COLLECTIONS.filter((c) => (c.popularity ?? 0) > 8)) {
        try {
          const isOwned = await manuallyCheckCollection(walletAddress, baseCollection.id)
          if (isOwned) {
            console.log(`Direct check confirmed ownership of ${baseCollection.name}`)
            const cloned: NFTCollection = { ...baseCollection, owned: true, tokenCount: 1 }
            ownedCollections.push(cloned)
          }
        } catch (err) {
          console.error(`Error in direct check for ${baseCollection.id}:`, err)
        }
      }
      
      if (ownedCollections.length > 0) {
        console.log(`Found ${ownedCollections.length} collections using direct checks`)
        return ownedCollections
      }
    }

    // Process the NFTs and match them against our known collections
    const collectionsWithOwnership = COLLECTIONS.map((collection) => ({ ...collection }))
    console.log(`🔍 Checking ${collectionsWithOwnership.length} collections against found contracts`)

    // Match contract addresses to our collections with full logic
    collectionsWithOwnership.forEach((collection) => {
      const contractAddress = collection.contractAddress.toLowerCase()
      const relatedAddresses = (collection.relatedContracts || []).map((addr) => addr.toLowerCase())
      const baseAddresses = (collection.baseContracts || []).map((addr) => addr.toLowerCase())
      const roninAddresses = (collection.roninContracts || []).map((addr) => addr.toLowerCase())
      
      // Debug logging for ALL collections to see if we reach this point
      if (collection.id === "ilya" && DEBUG_MODE) {
        console.log(`🔍 [DEBUG] Processing collection: ${collection.id} (${collection.name})`)
        console.log(`🔍 [DEBUG] contractAddress: ${contractAddress}`)
        console.log(`🔍 [DEBUG] baseAddresses: ${JSON.stringify(baseAddresses)}`)
        console.log(`🔍 [DEBUG] contractCounts keys: ${Object.keys(contractCounts).length}`)
      }

      // Debug logging for Moonbirds specifically
      if (collection.id === "moonbirds" && DEBUG_MODE) {
        console.log("🔍 [DEBUG] Moonbirds check:", {
          contractAddress,
          relatedAddresses,
          baseAddresses,
          roninAddresses,
          contractCounts: Object.keys(contractCounts),
          hasContractCount: !!contractCounts[contractAddress]
        })
      }

      // Debug logging for Ilya Kazakov specifically
      if (collection.id === "ilya" && DEBUG_MODE) {
        console.log("🔍 [DEBUG] Ilya Kazakov check:", {
          contractAddress,
          relatedAddresses,
          baseAddresses,
          roninAddresses,
          contractCounts: Object.keys(contractCounts),
          hasContractCount: !!contractCounts[contractAddress],
          targetBaseContract: "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9",
          hasTargetContract: !!contractCounts["0xa3fb1a28817c1c8ada52ac504dc320c40db782b9"]
        })

        // Check each address individually
        console.log("🔍 [DEBUG] Individual address checks:")
        console.log(`  - Main contract ${contractAddress}: ${contractCounts[contractAddress] || 0}`)
        relatedAddresses.forEach(addr => {
          console.log(`  - Related contract ${addr}: ${contractCounts[addr] || 0}`)
        })
        baseAddresses.forEach(addr => {
          console.log(`  - Base contract ${addr}: ${contractCounts[addr] || 0}`)
        })
      }

      // Skip Base chain collections until we're ready to support them
      // But allow specific collections with baseContracts to use Base chain
      if (collection.chain === "base" && !collection.baseContracts) {
        collection.owned = false
        collection.tokenCount = 0
        return
      }

      // For shared contracts (like OpenSea's), we need to check token IDs
      if (collection.tokenIdRange) {
        // Check if any tokens from this contract are within the specified range
        const tokensFromContract = tokenIdsByContract[contractAddress] || new Set()
        let matchingTokenCount = 0

        // Convert range boundaries to BigInt for proper comparison of large token IDs
        const rangeStart = BigInt(collection.tokenIdRange.start)
        const rangeEnd = BigInt(collection.tokenIdRange.end)

        if (DEBUG_MODE) {
          console.log(`🔍 Checking ${collection.name} token range:`, {
            start: collection.tokenIdRange.start,
            end: collection.tokenIdRange.end,
            tokensCount: tokensFromContract.size,
          })

          // Log all token IDs for debugging
          if (tokensFromContract.size > 0) {
            console.log(`🔍 Token IDs for ${collection.contractAddress}:`, Array.from(tokensFromContract))
          }
        }

        // Check each token ID to see if it's in the range for this collection
        tokensFromContract.forEach((tokenId) => {
          try {
            const tokenIdBigInt = BigInt(tokenId)
            if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
              matchingTokenCount++
              if (DEBUG_MODE) {
                console.log(`✅ Found matching token for ${collection.name}:`, tokenId)
              }
            }
          } catch (e) {
            // Skip if token ID can't be converted to BigInt
            console.error(`❌ Error comparing token ID ${tokenId}:`, e)
          }
        })

        if (matchingTokenCount > 0) {
          collection.owned = true
          collection.tokenCount = matchingTokenCount
          console.log(`🎉 ${collection.name} is owned with ${matchingTokenCount} tokens (token range)`)
        } else {
          console.log(`❌ ${collection.name} not found (token range check)`)
        }

        // Also check related contracts the same way
        relatedAddresses.forEach((relatedAddr) => {
          const tokensFromRelated = tokenIdsByContract[relatedAddr] || new Set()
          let relatedMatchingCount = 0

          tokensFromRelated.forEach((tokenId) => {
            try {
              const tokenIdBigInt = BigInt(tokenId)
              if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
                relatedMatchingCount++
                if (DEBUG_MODE) {
                  console.log(`✅ Found matching token for ${collection.name} in related contract:`, tokenId)
                }
              }
            } catch (e) {
              console.error(`❌ Error comparing token ID ${tokenId} for related contract:`, e)
            }
          })

          if (relatedMatchingCount > 0) {
            collection.owned = true
            collection.tokenCount = (collection.tokenCount || 0) + relatedMatchingCount
            console.log(`🎉 ${collection.name} is owned with ${relatedMatchingCount} additional tokens from related contract`)
          }
        })
      } else {
        // Standard contract check (non-shared contracts)
        let foundInMain = false
        let foundInRelated = false
        let foundInBase = false
        let foundInRonin = false
        
        // Check main contract address
        if (collection.id !== 'grifters' && contractCounts[contractAddress]) {
          collection.owned = true
          collection.tokenCount = contractCounts[contractAddress]
          foundInMain = true
          console.log(`🎉 ${collection.name} found in main contract: ${contractCounts[contractAddress]} tokens`)
          
          // Debug logging for Moonbirds specifically
          if (collection.id === "moonbirds" && DEBUG_MODE) {
            console.log("🚨 [DEBUG] Moonbirds marked as owned in main contract!")
          }
        }

        // Check related contract addresses
        if (collection.id !== 'grifters') {
          relatedAddresses.forEach((relatedAddr) => {
            if (contractCounts[relatedAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + contractCounts[relatedAddr]
              foundInRelated = true
              console.log(`🎉 ${collection.name} found in related contract ${relatedAddr}: ${contractCounts[relatedAddr]} tokens`)
            }
          })
        }

        // Check base contract addresses
        if (collection.id !== 'grifters') {
          baseAddresses.forEach((baseAddr) => {
            if (contractCounts[baseAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + contractCounts[baseAddr]
              foundInBase = true
              console.log(`🎉 ${collection.name} found in base contract ${baseAddr}: ${contractCounts[baseAddr]} tokens`)
              
          // Debug logging for Ilya Kazakov
          if (collection.id === "ilya" && DEBUG_MODE) {
            console.log(`🎯 [DEBUG] Ilya Kazakov marked as owned in base contract ${baseAddr}`)
          }
        } else {
          // Debug logging for Ilya Kazakov
          if (collection.id === "ilya" && DEBUG_MODE) {
            console.log(`❌ [DEBUG] Ilya Kazakov NOT found in base contract ${baseAddr}`)
          }
        }
          })
        }

        // Check ronin contract addresses
        if (collection.id !== 'grifters') {
          roninAddresses.forEach((roninAddr) => {
            if (contractCounts[roninAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + contractCounts[roninAddr]
              foundInRonin = true
              console.log(`🎉 ${collection.name} found in ronin contract ${roninAddr}: ${contractCounts[roninAddr]} tokens`)
            }
          })
        }

        if (!foundInMain && !foundInRelated && !foundInBase && !foundInRonin) {
          console.log(`❌ ${collection.name} not found in any contract`)

          // Debug logging for Ilya Kazakov
          if (collection.id === "ilya" && DEBUG_MODE) {
            console.log(`❌ [DEBUG] Ilya Kazakov FINAL RESULT: owned = false`)
          }
        } else {
          // Debug logging for Ilya Kazakov
          if (collection.id === "ilya" && DEBUG_MODE) {
            console.log(`🎯 [DEBUG] Ilya Kazakov FINAL RESULT: owned = true, tokenCount = ${collection.tokenCount}`)
          }
        }
      }
    })

    // Step 5.5: Initialize wallet list for special collection checks
    // For backward compatibility, only check the main wallet in the old function
    const allWalletsToCheck = [walletAddress]

    // Step 5.6: Special handling for specific collections (85%)
    onProgress?.({ current: 85, total: 100, collection: "Special collection checks..." })

    for (const currentWallet of allWalletsToCheck) {
      console.log(`🔍 Special collection checks for ${currentWallet === walletAddress ? 'main' : 'delegated'} wallet...`)

      // Special handling for Transmissions909 - force check with OpenSea API
      const transmissions909Collection = collectionsWithOwnership.find((c) => c.id === "transmissions909")
      if (transmissions909Collection && !transmissions909Collection.owned) {
        try {
          console.log("🔍 Special handling for Transmissions909...")
          const assets = await fetchOpenSeaAssets(currentWallet, "transmissions909")

          if (assets.length > 0) {
            console.log(`✅ Found ${assets.length} Transmissions909 assets from OpenSea API for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            transmissions909Collection.owned = true
            transmissions909Collection.tokenCount = (transmissions909Collection.tokenCount || 0) + assets.length
          }
        } catch (error) {
          console.error("❌ Error in special Transmissions909 handling:", error)
        }
      }

      // Special handling for Toy Faces - force check with OpenSea API
      const toyfacesCollection = collectionsWithOwnership.find((c) => c.id === "toyfaces")
      if (toyfacesCollection && !toyfacesCollection.owned) {
        try {
          console.log("🔍 Special handling for Toy Faces...")
          const assets = await fetchOpenSeaAssets(currentWallet, "toyfaces")

          if (assets.length > 0) {
            console.log(`✅ Found ${assets.length} Toy Faces assets from OpenSea API for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            toyfacesCollection.owned = true
            toyfacesCollection.tokenCount = (toyfacesCollection.tokenCount || 0) + assets.length
          }
        } catch (error) {
          console.error("❌ Error in special Toy Faces handling:", error)
        }
      }

      // Special handling for The Boneys - force check with OpenSea API
      const boneysCollection = collectionsWithOwnership.find((c) => c.id === "boneys")
      if (boneysCollection && !boneysCollection.owned) {
        try {
          console.log("🔍 Special handling for The Boneys...")
          const assets = await fetchOpenSeaAssets(currentWallet, "theboneys")

          if (assets.length > 0) {
            console.log(`✅ Found ${assets.length} The Boneys assets from OpenSea API for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            boneysCollection.owned = true
            boneysCollection.tokenCount = (boneysCollection.tokenCount || 0) + assets.length
          }
        } catch (error) {
          console.error("❌ Error in special The Boneys handling:", error)
        }
      }

      // Special handling for the#boi (boiz) - force check with OpenSea API
      const boizCollection = collectionsWithOwnership.find((c) => c.id === "boiz")
      if (boizCollection && !boizCollection.owned) {
        try {
          console.log("🔍 Special handling for the#boi (boiz)...")
          const assets = await fetchOpenSeaAssets(currentWallet, "the-boi")

          if (assets.length > 0) {
            console.log(`✅ Found ${assets.length} the#boi assets from OpenSea API for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            boizCollection.owned = true
            boizCollection.tokenCount = (boizCollection.tokenCount || 0) + assets.length
          }
        } catch (error) {
          console.error("❌ Error in special the#boi handling:", error)
        }
      }

      // Special handling for Based Minis - direct contract check
      const basedMinisCollection = collectionsWithOwnership.find((c) => c.id === "based-minis")
      if (basedMinisCollection && !basedMinisCollection.owned) {
        try {
          console.log("🔍 Special handling for Based Minis...")

          // Create a minimal ERC721 interface to check balance
          const erc721Interface = new ethers.Interface(["function balanceOf(address owner) view returns (uint256)"])

          const contractAddress = "0xd683f8f08aca669bf9207d72dceb6bac063c34a2" // Based Minis contract
          const baseProvider = new ethers.JsonRpcProvider("https://mainnet.base.org")
          const contract = new ethers.Contract(contractAddress, erc721Interface, baseProvider)

          // Check if wallet owns any tokens from this collection
          let balanceNumber = 0
          try {
            // Add null check for contract and provider
            if (!contract || !contract.balanceOf) {
              console.warn(`⚠️ Contract not properly initialized for Based Minis`)
              return collectionsWithOwnership
            }
            const balance = await contract.balanceOf(currentWallet)
            balanceNumber = Number(balance)
          } catch (err) {
            console.warn(`⚠️ Error calling balanceOf on Based Minis contract (${contractAddress}):`, err)
          }
          if (balanceNumber > 0) {
            console.log(`✅ Found ${balanceNumber} Based Minis tokens via direct contract check for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            basedMinisCollection.owned = true
            basedMinisCollection.tokenCount = (basedMinisCollection.tokenCount || 0) + balanceNumber
          }
        } catch (error) {
          console.error("❌ Error in special Based Minis handling:", error)
        }
      }

      // Special handling for re:generates (Base chain) - direct contract check
      const regeneratesCollection = collectionsWithOwnership.find((c) => c.id === "regenerates")
      if (regeneratesCollection && !regeneratesCollection.owned) {
        try {
          console.log("🔍 Special handling for re:generates (Base chain)...")
          const erc721Interface = new ethers.Interface(["function balanceOf(address owner) view returns (uint256)"])
          const contractAddress = "0x56dfe6ae26bf3043dc8fdf33bf739b4ff4b3bc4a" // re:generates contract
          const baseProvider = new ethers.JsonRpcProvider("https://mainnet.base.org")
          const contract = new ethers.Contract(contractAddress, erc721Interface, baseProvider)
          let balanceNumber = 0
          try {
            // Add null check for contract and provider
            if (!contract || !contract.balanceOf) {
              console.warn(`⚠️ Contract not properly initialized for re:generates`)
              return collectionsWithOwnership
            }
            const balance = await contract.balanceOf(currentWallet)
            balanceNumber = Number(balance)
          } catch (err) {
            console.warn(`⚠️ Error calling balanceOf on re:generates contract (${contractAddress}):`, err)
          }
          if (balanceNumber > 0) {
            console.log(`✅ Found ${balanceNumber} re:generates tokens via direct contract check for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
          regeneratesCollection.owned = true
          regeneratesCollection.tokenCount = balanceNumber
        }
      } catch (error) {
        console.error("❌ Error in special re:generates handling:", error)
      }
    }

      // Special handling for Based Fellas (Base chain) - direct contract check
      const basedFellasCollection = collectionsWithOwnership.find((c) => c.id === "basedfellas")
      if (basedFellasCollection && !basedFellasCollection.owned) {
        try {
          console.log("🔍 Special handling for Based Fellas (Base chain)...")
          const erc721Interface = new ethers.Interface(["function balanceOf(address owner) view returns (uint256)"])
          const contractAddress = "0x217ec1ac929a17481446a76ff9b95b9a64f298cf" // Based Fellas contract
          const baseProvider = new ethers.JsonRpcProvider("https://mainnet.base.org")
          const contract = new ethers.Contract(contractAddress, erc721Interface, baseProvider)
          let balanceNumber = 0
          try {
            // Add null check for contract and provider
            if (!contract || !contract.balanceOf) {
              console.warn(`⚠️ Contract not properly initialized for Based Fellas`)
              return collectionsWithOwnership
            }
            const balance = await contract.balanceOf(currentWallet)
            balanceNumber = Number(balance)
          } catch (err) {
            console.warn(`⚠️ Error calling balanceOf on Based Fellas contract (${contractAddress}):`, err)
          }
          if (balanceNumber > 0) {
            console.log(`✅ Found ${balanceNumber} Based Fellas tokens via direct contract check for ${currentWallet.slice(0, 6)}...${currentWallet.slice(-4)}`)
            basedFellasCollection.owned = true
            basedFellasCollection.tokenCount = (basedFellasCollection.tokenCount || 0) + balanceNumber
          }
        } catch (error) {
          console.error("❌ Error in special Based Fellas handling:", error)
        }
      }
    }

    // Step 6: Complete (100%)
    onProgress?.({ current: 100, total: 100, collection: "Complete!" })

    // Filter to only return owned collections
    let ownedCollections = collectionsWithOwnership.filter((collection) => collection.owned && !collection.hidden)

    // If we couldn't find any collections, check if this is a known issue with the API
    // and provide a fallback for testing
    if (ownedCollections.length === 0 && process.env.NODE_ENV === "production") {
      console.warn("No collections found for wallet. Checking if this is a known wallet with collections...")

      // Check if this wallet is known to have collections but our API calls failed
      const knownWallets = [
        "0x8fa217251049c207Eee65f0a2eAa2D5C4BF4e5D0", // Example wallet known to have Azuki
        "0xd387a6e4e84a6c86bd90c158c6028a58cc8ac459", // Example wallet known to have BAYC
        // Add more known wallets if needed
      ]

      if (knownWallets.some((w) => w.toLowerCase() === walletAddress.toLowerCase())) {
        console.log("This is a known wallet. Attempting direct collection checks...")

        // Try direct contract checks for popular collections
        for (const collection of COLLECTIONS.filter((c) => (c.popularity ?? 0) > 8)) {
          try {
            const isOwned = await manuallyCheckCollection(walletAddress, collection.id)
            if (isOwned) {
              console.log(`Direct check confirmed ownership of ${collection.name}`)
              collection.owned = true
              collection.tokenCount = 1
              ownedCollections.push(collection)
            }
          } catch (err) {
            console.error(`Error in direct check for ${collection.id}:`, err)
          }
        }
      }
    }

    // Note: Removed Grifters final guard logic (replaced with Lil Pudgys)

    console.log(`Found ${ownedCollections.length} owned collections using TRUE parallelization`)
    return ownedCollections

  } catch (error) {
    console.error("Error in TRUE parallel collection check:", error)
    throw error
  }
}

/**
 * Enhanced main function that checks wallet NFT collections across multiple blockchains in parallel
 * including delegated wallets from Delegate.xyz API
 *
 * @param walletAddress - The wallet address to check for NFT ownership
 * @param onProgress - Optional callback function to track progress (0-100%)
 * @returns Promise<NFTCollection[]> - Array of owned collections with token counts
 *
 * This function performs the following steps:
 * 1. Fetches delegated wallets from Delegate.xyz API
 * 2. Resolves ENS domains to ETH addresses
 * 3. Searches all wallets (main + delegated) in parallel
 * 4. Merges results from all wallets
 * 5. Matches against the COLLECTIONS array to determine ownership
 */
export async function checkCollectionsWithDelegatedWallets(
  walletAddress: string,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<NFTCollection[]> {
  console.log(`🚀 Starting enhanced parallel collection check for ${walletAddress}`)

  try {
    // Step 1: Initialize (5%)
    onProgress?.({
      current: 5,
      total: 100,
      collection: "Initializing..."
    })

    console.log(`🔍 Fetching delegated wallets from Delegate.xyz API...`)
    const delegatedWallets = await fetchDelegatedWallets(walletAddress)
    console.log(`✅ Found ${delegatedWallets.length} delegated wallets`)

    // Create allWalletsToCheck IMMEDIATELY after delegatedWallets - this MUST be first!
    const allWalletsToCheck = [walletAddress, ...delegatedWallets.map(w => w.address)]

    // Step 2: Fetch delegated wallets (15%)
    onProgress?.({
      current: 15,
      total: 100,
      collection: "Fetching delegated wallets..."
    })

    // Step 3: Resolve ENS domains (25%)
    onProgress?.({
      current: 25,
      total: 100,
      collection: "Resolving ENS domains..."
    })

    const allWalletAddresses: string[] = [walletAddress]
    const ensResolutions: Promise<void>[] = []

    for (const wallet of delegatedWallets) {
      if (wallet.address.includes('.eth')) {
        // ENS domain - resolve to address
        const resolutionPromise = resolveEnsToAddress(wallet.address).then((resolvedAddress) => {
          if (resolvedAddress && !allWalletAddresses.includes(resolvedAddress)) {
            allWalletAddresses.push(resolvedAddress)
          }
        })
        ensResolutions.push(resolutionPromise)
      } else {
        // Already an ETH address
        if (!allWalletAddresses.includes(wallet.address)) {
          allWalletAddresses.push(wallet.address)
        }
      }
    }

    // Wait for all ENS resolutions to complete
    await Promise.allSettled(ensResolutions)

    console.log(`📋 Total wallets to search: ${allWalletAddresses.length}`)
    console.log(`🔍 Main wallet: ${walletAddress}`)
    if (allWalletAddresses.length > 1) {
      console.log(`🔗 Delegated wallets: ${allWalletAddresses.slice(1).join(', ')}`)
    }

    // Step 4: Search all wallets (main + delegated) (35-85%)
    onProgress?.({
      current: 35,
      total: 100,
      collection: "Searching all wallets..."
    })

    const searchResult = await searchMultipleWalletsWithDelegationAwareness(
      walletAddress,
      allWalletAddresses,
      delegatedWallets,
      onProgress
    )

    // Step 5: Match collections (90%)
    onProgress?.({
      current: 90,
      total: 100,
      collection: "Matching collections..."
    })

    console.log(`🎯 Matching ${Object.keys(searchResult.contractCounts).length} contracts against ${COLLECTIONS.length} collections`)

    // Step 6: Complete (100%)
    onProgress?.({
      current: 100,
      total: 100,
      collection: "Complete!"
    })

    // Process the NFTs and match them against our known collections
    const collectionsWithOwnership = COLLECTIONS.map((collection) => ({ ...collection }))

    // Match contract addresses to our collections with full logic
    collectionsWithOwnership.forEach((collection) => {
      const contractAddress = collection.contractAddress.toLowerCase()
      const relatedAddresses = (collection.relatedContracts || []).map((addr) => addr.toLowerCase())
      const baseAddresses = (collection.baseContracts || []).map((addr) => addr.toLowerCase())
      const roninAddresses = (collection.roninContracts || []).map((addr) => addr.toLowerCase())

      // Skip Base chain collections until we're ready to support them
      // But allow specific collections with baseContracts to use Base chain
      if (collection.chain === "base" && !collection.baseContracts) {
        collection.owned = false
        collection.tokenCount = 0
        return
      }

      // For shared contracts (like OpenSea's), we need to check token IDs
      if (collection.tokenIdRange) {
        // Check if any tokens from this contract are within the specified range
        const tokensFromContract = searchResult.tokenIdsByContract[contractAddress] || new Set()
        let matchingTokenCount = 0

        // Convert range boundaries to BigInt for proper comparison of large token IDs
        const rangeStart = BigInt(collection.tokenIdRange.start)
        const rangeEnd = BigInt(collection.tokenIdRange.end)

        // Check each token ID to see if it's in the range for this collection
        tokensFromContract.forEach((tokenId) => {
          try {
            const tokenIdBigInt = BigInt(tokenId)
            if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
              matchingTokenCount++
            }
          } catch (e) {
            // Skip if token ID can't be converted to BigInt
            console.error(`❌ Error comparing token ID ${tokenId}:`, e)
          }
        })

        if (matchingTokenCount > 0) {
          collection.owned = true
          collection.tokenCount = matchingTokenCount
          console.log(`🎉 ${collection.name} is owned with ${matchingTokenCount} tokens (token range)`)
        }

        // Also check related contracts the same way
        relatedAddresses.forEach((relatedAddr) => {
          const tokensFromRelated = searchResult.tokenIdsByContract[relatedAddr] || new Set()
          let relatedMatchingCount = 0

          tokensFromRelated.forEach((tokenId) => {
            try {
              const tokenIdBigInt = BigInt(tokenId)
              if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
                relatedMatchingCount++
              }
            } catch (e) {
              console.error(`❌ Error comparing token ID ${tokenId} for related contract:`, e)
            }
          })

          if (relatedMatchingCount > 0) {
            collection.owned = true
            collection.tokenCount = (collection.tokenCount || 0) + relatedMatchingCount
          }
        })
      } else {
        // Standard contract check (non-shared contracts)
        let foundInMain = false
        let foundInRelated = false
        let foundInBase = false
        let foundInRonin = false

        // Check main contract address
        if (collection.id !== 'grifters' && searchResult.contractCounts[contractAddress]) {
          collection.owned = true
          collection.tokenCount = searchResult.contractCounts[contractAddress]
          foundInMain = true
          console.log(`🎉 ${collection.name} found in main contract: ${searchResult.contractCounts[contractAddress]} tokens`)
        }

        // Check related contract addresses
        if (collection.id !== 'grifters') {
          relatedAddresses.forEach((relatedAddr) => {
            if (searchResult.contractCounts[relatedAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + searchResult.contractCounts[relatedAddr]
              foundInRelated = true
              console.log(`🎉 ${collection.name} found in related contract ${relatedAddr}: ${searchResult.contractCounts[relatedAddr]} tokens`)
            }
          })
        }

        // Check Base chain contracts
        if (collection.baseContracts) {
          baseAddresses.forEach((baseAddr) => {
            if (searchResult.contractCounts[baseAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + searchResult.contractCounts[baseAddr]
              foundInBase = true
              console.log(`🎉 ${collection.name} found in Base contract ${baseAddr}: ${searchResult.contractCounts[baseAddr]} tokens`)
            }
          })
        }

        // Check Ronin chain contracts
        if (collection.roninContracts) {
          roninAddresses.forEach((roninAddr) => {
            if (searchResult.contractCounts[roninAddr]) {
              collection.owned = true
              collection.tokenCount = (collection.tokenCount || 0) + searchResult.contractCounts[roninAddr]
              foundInRonin = true
              console.log(`🎉 ${collection.name} found in Ronin contract ${roninAddr}: ${searchResult.contractCounts[roninAddr]} tokens`)
            }
          })
        }
      }
    })

    // Step 5.6: Special handling for specific collections (85%)
    onProgress?.({ current: 85, total: 100, collection: "Special collection checks..." })

    // Special handling for Transmissions909 - force check with OpenSea API
    const transmissions909Collection = collectionsWithOwnership.find((c) => c.id === "transmissions909")
    if (transmissions909Collection && !transmissions909Collection.owned) {
      try {
        console.log("🔍 Special handling for Transmissions909...")
        const assets = await fetchOpenSeaAssets(walletAddress, "transmissions909")

        if (assets.length > 0) {
          console.log(`✅ Found ${assets.length} Transmissions909 assets from OpenSea API for ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`)
          transmissions909Collection.owned = true
          transmissions909Collection.tokenCount = (transmissions909Collection.tokenCount || 0) + assets.length
        }
      } catch (error) {
        console.error("❌ Error in special Transmissions909 handling:", error)
      }
    }

    // Special handling for Toy Faces - force check with OpenSea API
    const toyfacesCollection = collectionsWithOwnership.find((c) => c.id === "toyfaces")
    if (toyfacesCollection && !toyfacesCollection.owned) {
      try {
        console.log("🔍 Special handling for Toy Faces...")
        const assets = await fetchOpenSeaAssets(walletAddress, "toyfaces")

        if (assets.length > 0) {
          console.log(`✅ Found ${assets.length} Toy Faces assets from OpenSea API for ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`)
          toyfacesCollection.owned = true
          toyfacesCollection.tokenCount = (toyfacesCollection.tokenCount || 0) + assets.length
        }
      } catch (error) {
        console.error("❌ Error in special Toy Faces handling:", error)
      }
    }

    // Special handling for The Boneys - force check with OpenSea API
    const boneysCollection = collectionsWithOwnership.find((c) => c.id === "boneys")
    if (boneysCollection && !boneysCollection.owned) {
      try {
        console.log("🔍 Special handling for The Boneys...")
        const assets = await fetchOpenSeaAssets(walletAddress, "theboneys")

        if (assets.length > 0) {
          console.log(`✅ Found ${assets.length} The Boneys assets from OpenSea API for ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`)
          boneysCollection.owned = true
          boneysCollection.tokenCount = (boneysCollection.tokenCount || 0) + assets.length
        }
      } catch (error) {
        console.error("❌ Error in special The Boneys handling:", error)
      }
    }

    // Filter to only owned collections and return
    const ownedCollections = collectionsWithOwnership.filter((collection) => collection.owned)

    console.log(`🎯 Final result: ${ownedCollections.length} owned collections out of ${collectionsWithOwnership.length} total`)

    if (DEBUG_MODE) {
      ownedCollections.forEach((collection) => {
        console.log(`✅ ${collection.name}: ${collection.tokenCount} tokens`)
      })
    }

    // Note: ENS ownership is already checked in searchSingleWallet() and integrated
    // into the normal collection processing pipeline via walletResult.contractCounts
    // No need for duplicate ENS check here - it would cause race conditions

    onProgress?.({
      current: 100,
      total: 100,
      collection: "Complete!"
    })

    return ownedCollections

  } catch (error) {
    console.error("❌ Error in enhanced parallel collection check:", error)
    throw error
  }
}

/**
 * Test function to verify delegation functionality
 * This function helps debug delegation issues by testing various aspects
 */
export async function testDelegationIntegration(walletAddress: string): Promise<{
  success: boolean;
  delegatedWallets: DelegateWallet[];
  delegationChecks: Record<string, boolean>;
  errors: string[];
}> {
  const result = {
    success: false,
    delegatedWallets: [] as DelegateWallet[],
    delegationChecks: {} as Record<string, boolean>,
    errors: [] as string[]
  }

  console.log(`🧪 Testing delegation integration for ${walletAddress}`)

  try {
    // Step 1: Fetch delegated wallets
    console.log('📋 Step 1: Fetching delegated wallets...')
    const delegatedWallets = await fetchDelegatedWallets(walletAddress)

    if (delegatedWallets.length === 0) {
      console.log('⚠️ No delegated wallets found')
    } else {
      console.log(`✅ Found ${delegatedWallets.length} delegated wallets:`)
      delegatedWallets.forEach(wallet => {
        console.log(`  - ${wallet.address} (${wallet.type})`)
        console.log(`    Permissions: ${wallet.permissions.length} delegations`)
        wallet.permissions.forEach(perm => {
          console.log(`      → ${perm.type} delegation ${perm.from} → ${perm.to}${perm.contract ? ` for ${perm.contract}` : ''}`)
        })
      })
    }

    result.delegatedWallets = delegatedWallets

    // Step 2: Test delegation check functions
    console.log('\n🔍 Step 2: Testing delegation check functions...')

    // Test check all delegation
    try {
      const checkAllResult = await checkDelegationAll(walletAddress, delegatedWallets[0]?.address || '0x0000000000000000000000000000000000000000')
      result.delegationChecks['checkAll'] = checkAllResult
      console.log(`✅ Check All delegation: ${checkAllResult}`)
    } catch (error) {
      result.errors.push(`Check All delegation error: ${error}`)
      console.log(`❌ Check All delegation error: ${error}`)
    }

    // Step 3: Test ENS resolution if needed
    console.log('\n🏷️ Step 3: Testing ENS resolution...')
    for (const wallet of delegatedWallets) {
      if (wallet.address.includes('.eth')) {
        try {
          const resolvedAddress = await resolveEnsToAddress(wallet.address)
          console.log(`✅ ENS ${wallet.address} resolved to: ${resolvedAddress}`)
        } catch (error) {
          result.errors.push(`ENS resolution error for ${wallet.address}: ${error}`)
          console.log(`❌ ENS resolution error for ${wallet.address}: ${error}`)
        }
      }
    }

    // Step 4: Test actual collection check
    console.log('\n🎯 Step 4: Testing actual collection check...')
    try {
      const collections = await checkCollectionsWithDelegatedWallets(walletAddress)
      console.log(`✅ Found ${collections.length} collections`)
      collections.forEach(collection => {
        if (collection.owned) {
          console.log(`  - ${collection.name}: ${collection.tokenCount} tokens`)
        }
      })
    } catch (error) {
      result.errors.push(`Collection check error: ${error}`)
      console.log(`❌ Collection check error: ${error}`)
    }

    result.success = result.errors.length === 0
    console.log(`\n${result.success ? '✅' : '❌'} Delegation test ${result.success ? 'PASSED' : 'FAILED'}`)

    if (result.errors.length > 0) {
      console.log('Errors encountered:')
      result.errors.forEach(error => console.log(`  - ${error}`))
    }

    return result

  } catch (error) {
    result.errors.push(`Test error: ${error}`)
    console.log(`❌ Test error: ${error}`)
    return result
  }
}
